import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { Award, TrendingDown, PiggyBank, Info } from 'lucide-react';
import type { CacComparisonResponse } from '../../types/admin-partner';
import { formatBrl } from './ExecutiveMetricCard';
import {
  Tooltip as UiTooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';

interface PartnerCacComparisonChartProps {
  data?: CacComparisonResponse;
  isLoading?: boolean;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const item = payload[0]?.payload;
    return (
      <div className="bg-popover/95 backdrop-blur-sm border border-border p-3 rounded-lg shadow-lg text-xs space-y-1.5 min-w-[200px]">
        <div className="font-semibold text-foreground border-b border-border/60 pb-1">
          {label}
        </div>
        <div className="flex justify-between items-center text-emerald-600 dark:text-emerald-400 font-medium">
          <span>CAC Parceiros:</span>
          <span>{formatBrl(item?.partnerCac || 0)}</span>
        </div>
        <div className="flex justify-between items-center text-slate-500 dark:text-slate-400 font-medium">
          <span>CAC Canal Direto (Orgânico):</span>
          <span>{formatBrl(item?.organicCac || 0)}</span>
        </div>
        <div className="flex justify-between items-center text-primary font-bold pt-1 border-t border-border/50">
          <span>Economia Unitária:</span>
          <span>{formatBrl((item?.organicCac || 0) - (item?.partnerCac || 0))}</span>
        </div>
        <div className="flex justify-between items-center text-muted-foreground pt-0.5">
          <span>Aquisições via Parceiros:</span>
          <span>{item?.acquisitionsPartner || 0} pacientes</span>
        </div>
      </div>
    );
  }
  return null;
};

export const PartnerCacComparisonChart: React.FC<PartnerCacComparisonChartProps> = ({
  data,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 shadow-xs animate-pulse space-y-4">
        <div className="h-6 w-56 bg-muted rounded"></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 bg-muted/60 rounded-lg"></div>
          ))}
        </div>
        <div className="h-64 bg-muted/40 rounded-lg"></div>
      </div>
    );
  }

  const summary = data?.summary || {
    averagePartnerCac: 22,
    averageOrganicCac: 45,
    totalSavingsCents: 0,
    totalSavings: 0,
    savingsPercent: 51.1,
    totalPartnerAcquisitions: 0,
  };

  const trend = data?.trend || [];
  const unitSavings = Math.max(0, summary.averageOrganicCac - summary.averagePartnerCac);

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-xs flex flex-col justify-between">
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border/60">
          <div>
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <PiggyBank className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              Eficiência de Aquisição: CAC Parceiros vs Canal Direto
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Comparativo de custo por aquisição e economia financeira proporcionada pelo programa de afiliados
            </p>
          </div>

          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 rounded-lg text-xs font-semibold self-start sm:self-auto">
            <TrendingDown className="h-4 w-4" />
            Economia de {summary.savingsPercent}% no Custo de Aquisição
          </div>
        </div>

        {/* 4 Cards de Destaque CAC */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-4">
          <div className="p-3 bg-emerald-50/50 dark:bg-emerald-950/20 rounded-lg border border-emerald-100 dark:border-emerald-900/40">
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400 block truncate">
                CAC Médio Parceiros
              </span>
              <TooltipProvider delayDuration={150}>
                <UiTooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-emerald-700/60 hover:text-emerald-700 transition-colors p-0.5 rounded-full hover:bg-emerald-100 cursor-pointer shrink-0">
                      <Info className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px] p-2.5 text-xs bg-slate-900 text-slate-100 rounded-xl">
                    Custo por Paciente: Valor médio de comissão pago na 1ª consulta do paciente indicado.
                  </TooltipContent>
                </UiTooltip>
              </TooltipProvider>
            </div>
            <span className="text-xl font-bold text-emerald-700 dark:text-emerald-300">
              {formatBrl(summary.averagePartnerCac)}
            </span>
            <span className="text-[10px] text-muted-foreground block mt-0.5">
              Por paciente ativado
            </span>
          </div>

          <div className="p-3 bg-muted/40 rounded-lg border border-border/50">
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span className="text-[11px] font-medium text-muted-foreground block truncate">
                CAC Canal Direto (Benchmark)
              </span>
              <TooltipProvider delayDuration={150}>
                <UiTooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground/60 hover:text-primary transition-colors p-0.5 rounded-full hover:bg-muted cursor-pointer shrink-0">
                      <Info className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px] p-2.5 text-xs bg-slate-900 text-slate-100 rounded-xl">
                    Custo de Mercado: Custo padrão de mídia paga (Google/Meta Ads) para adquirir um paciente (R$ 45,00).
                  </TooltipContent>
                </UiTooltip>
              </TooltipProvider>
            </div>
            <span className="text-xl font-bold text-foreground">
              {formatBrl(summary.averageOrganicCac)}
            </span>
            <span className="text-[10px] text-muted-foreground block mt-0.5">
              Mídia paga + tráfego direto
            </span>
          </div>

          <div className="p-3 bg-blue-50/50 dark:bg-blue-950/20 rounded-lg border border-blue-100 dark:border-blue-900/40">
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span className="text-[11px] font-medium text-blue-700 dark:text-blue-400 block truncate">
                Economia por Paciente
              </span>
              <TooltipProvider delayDuration={150}>
                <UiTooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-blue-700/60 hover:text-blue-700 transition-colors p-0.5 rounded-full hover:bg-blue-100 cursor-pointer shrink-0">
                      <Info className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px] p-2.5 text-xs bg-slate-900 text-slate-100 rounded-xl">
                    Diferença entre o custo de anúncios pagos e a comissão de parceiros por paciente.
                  </TooltipContent>
                </UiTooltip>
              </TooltipProvider>
            </div>
            <span className="text-xl font-bold text-blue-700 dark:text-blue-300">
              {formatBrl(unitSavings)}
            </span>
            <span className="text-[10px] text-muted-foreground block mt-0.5">
              {summary.savingsPercent}% mais barato
            </span>
          </div>

          <div className="p-3 bg-purple-50/50 dark:bg-purple-950/20 rounded-lg border border-purple-100 dark:border-purple-900/40">
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span className="text-[11px] font-medium text-purple-700 dark:text-purple-400 block truncate">
                Economia Total Gerada
              </span>
              <TooltipProvider delayDuration={150}>
                <UiTooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-purple-700/60 hover:text-purple-700 transition-colors p-0.5 rounded-full hover:bg-purple-100 cursor-pointer shrink-0">
                      <Info className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px] p-2.5 text-xs bg-slate-900 text-slate-100 rounded-xl">
                    Soma total de economia em marketing gerada pelo programa de indicação no período.
                  </TooltipContent>
                </UiTooltip>
              </TooltipProvider>
            </div>
            <span className="text-xl font-bold text-purple-700 dark:text-purple-300">
              {formatBrl(summary.totalSavings)}
            </span>
            <span className="text-[10px] text-muted-foreground block mt-0.5">
              {summary.totalPartnerAcquisitions} pacientes via parceiros
            </span>
          </div>
        </div>

        {/* Gráfico comparativo Recharts */}
        <div className="h-64 w-full mt-2">
          {trend.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
              Sem dados históricos de CAC suficientes para o período
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/60" />
                <XAxis
                  dataKey="label"
                  stroke="currentColor"
                  className="text-[11px] text-muted-foreground"
                  tickLine={false}
                  axisLine={{ stroke: 'currentColor', opacity: 0.2 }}
                />
                <YAxis
                  stroke="currentColor"
                  className="text-[11px] text-muted-foreground"
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(val) => `R$${val}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="top"
                  align="right"
                  iconType="circle"
                  wrapperStyle={{ paddingBottom: '12px', fontSize: '12px' }}
                />
                <Bar
                  dataKey="partnerCac"
                  name="CAC Parceiros (R$)"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={32}
                />
                <Bar
                  dataKey="organicCac"
                  name="CAC Canal Direto (R$)"
                  fill="#94a3b8"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={32}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-border/60 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          * O CAC de Parceiros é composto estritamente pelas comissões remuneradas na 1ª conversão do paciente
        </span>
        <div className="flex items-center gap-1 font-semibold text-emerald-600 dark:text-emerald-400">
          <Award className="h-4 w-4" />
          Programa com ROI Positivo & CAC Blindado
        </div>
      </div>
    </div>
  );
};
